/*
  7Raw home stage (sections/7r-home-stage.liquid; UPDATE-02 items 1-3, plan 2026-10-13 §4; prototype v7
  L978-997). <r7-stage> sits inside <section class="r7-stage"> and drives its server-rendered screens.
  - Active screen and in-view (passive scroll + resize, one rAF per frame): with n screens,
    index = clamp(round(-top / (height / n)), 0, n - 1); the stage is in view while the section's bottom
    is below 55% of the viewport (the contract's rule, v7 `inn`) and its top is above 45% of it. On the
    home page the stage leads (its top starts at 0, or just below the header group with overlap off) and
    only moves up from there, so the top test always holds: the result there is exactly the contract's.
    The top test only matters for a stage placed lower on a page, which must not set the header tone,
    the snap, the rail or playback before it is reached.
    7r-header.js applies the same two-sided rule until the stage first reports.
  - Header contract: while in view, body[data-stage-tone] holds the active screen's tone ('dark' | 'light');
    out of view or disconnected the attribute is removed. Every change also dispatches document
    'r7:stage-change' with detail { inView, index, tone, hasVideo }. The header (7r-header.js) publishes
    html.r7-overlay-open and html.r7-bag-open; 7r-stage.css uses them to hide the rail and Pause.
  - Rail: .is-active and aria-current on the active item; a click scrolls to sectionTop + i * (height / n).
    The active bar's fill shows currentTime / duration on a video screen, full on an image screen, empty
    elsewhere. On a video screen it is one Web Animation (linear scaleY 0 → 1, one film loop long, endless)
    that the compositor runs: no per-frame script. It is set to the film's time and played or paused
    with it on playing / pause / waiting / seeking / seeked (also each loop wrap) / ratechange /
    durationchange / loadedmetadata and when the active screen changes; 'timeupdate' (about 4 per second)
    only corrects a drift over DRIFT_MS. Without Element.animate the fill follows 'timeupdate'.
  - Video (7r-film.js semantics, but playback follows the active screen, not visibility): only the
    active screen's video plays, and only while the stage is in view and the page is visible. Sources
    are attached after window load (the poster shows until then): data-src-small below 760px, data-src-large
    from 760px, for the active and the next video screen. Only a blocked autoplay (NotAllowedError)
    counts as a pause; a play() aborted by a pause (AbortError) does not. A visitor's pause is global and
    sticks until Play. It starts paused (poster + Play) when R7.prefersReducedMotion().
  - Scroll snap: html scroll-snap-type 'y proximity' only on the home page while the stage is in view,
    with the section's snap setting on, never in the theme editor or under R7.prefersReducedMotion().
  - Keyboard focus that enters a screen out of its slot scrolls that screen into its slot, so the focus
    ring is never hidden under a stacked sticky screen.
  - Carnet Noir: "Open the notebook" ([data-r7-notebook-open]) is handled only by
    assets/7r-notebook-loader.js, which the section loads with it (one path, so never a double open);
    without the loader the link opens the Carnet Noir page with #notebook.
  - Under-header offset: with data-overlap-header on the section, .r7-stage--lead stays only while the
    section is main's first .shopify-section (section.index is nil in the editor).
  - Theme editor: shopify:block:select scrolls to that screen; section load/unload/reorder re-sync.
  Every listener is bound with one AbortController per connection (7r-hero.js pattern): disconnecting
  aborts it, clears the snap and the body attribute, so the editor's section reload rebinds cleanly.
*/
(() => {
  if (customElements.get('r7-stage')) return;

  /* Same ratios as STAGE_BOTTOM_RATIO / STAGE_TOP_RATIO in 7r-header.js. */
  const IN_VIEW_BOTTOM = 0.55;
  const IN_VIEW_TOP = 0.45;
  const WIDE_QUERY = '(min-width: 760px)';
  /* Rail fill on a video screen. */
  const FILL_FRAMES = [{ transform: 'scaleY(0)' }, { transform: 'scaleY(1)' }];
  const DRIFT_MS = 120;
  const FILL_SYNC_EVENTS = [
    'pause',
    'waiting',
    'seeking',
    'seeked',
    'ratechange',
    'durationchange',
    'loadedmetadata',
  ];

  // Calls fn once the page has had its first contentful paint (or right away if it already has, or if the
  // browser can't tell). A 2s cap covers tabs that load hidden and never paint until shown.
  const afterFirstPaint = (fn) => {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      fn();
    };
    const painted = () =>
      typeof performance.getEntriesByType === 'function' &&
      performance.getEntriesByType('paint').some((entry) => entry.name === 'first-contentful-paint');
    if (painted() || typeof PerformanceObserver !== 'function') return go();
    try {
      const observer = new PerformanceObserver((list) => {
        if (list.getEntries().some((entry) => entry.name === 'first-contentful-paint')) {
          observer.disconnect();
          go();
        }
      });
      observer.observe({ type: 'paint', buffered: true });
      setTimeout(() => {
        observer.disconnect();
        go();
      }, 2000);
    } catch (error) {
      go();
    }
  };

  const reducedMotion = () =>
    window.R7
      ? window.R7.prefersReducedMotion()
      : document.documentElement.classList.contains('r7-rm') &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  class R7Stage extends HTMLElement {
    connectedCallback() {
      this.teardown();

      this.section = this.closest('.r7-stage') || this;
      this.screens = Array.from(this.querySelectorAll('[data-r7-stage-screen]'));
      this.count = this.screens.length;

      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.syncLead();
      this.bindSections();
      if (!this.count) return;

      this.tones = this.screens.map((screen) => (screen.dataset.tone === 'light' ? 'light' : 'dark'));
      this.videos = this.screens.map((screen) => screen.querySelector('[data-r7-stage-video]'));
      this.dots = Array.from(this.querySelectorAll('[data-r7-stage-dot]'));
      this.fills = this.dots.map((dot) => dot.querySelector('[data-r7-stage-fill]'));
      this.toggle = this.querySelector('[data-r7-stage-toggle]');

      this.wide = window.matchMedia(WIDE_QUERY);
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.snapEnabled = this.dataset.snap === 'true' && this.dataset.home === 'true';
      this.userPaused = reducedMotion();
      this.loaded = document.readyState === 'complete';
      this.index = -1;
      this.inView = false;
      this.rendered = false;
      this.snapOn = false;
      this.frame = 0;
      this.fillAnimation = null;

      this.bindVideos();
      this.bindScroll();
      this.bindRail();
      this.bindToggle();
      this.bindFocus();
      this.bindEditor();

      this.update();
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.stopFillAnimation();
      this.abort?.abort();
      this.abort = null;

      if (this.snapOn) {
        document.documentElement.style.scrollSnapType = '';
        this.snapOn = false;
      }
      this.videos?.forEach((video) => {
        if (video && !video.paused) video.pause();
      });
      if (this.inView) {
        this.inView = false;
        delete document.body.dataset.stageTone;
        this.announce();
      }
    }

    /* ---------- Position: active screen and in-view ---------- */

    bindScroll() {
      const schedule = () => {
        if (!this.frame) {
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.update();
          });
        }
      };
      window.addEventListener('scroll', schedule, { passive: true, signal: this.signal });
      window.addEventListener('resize', schedule, { passive: true, signal: this.signal });
      window.addEventListener(
        'pageshow',
        () => {
          this.update();
          this.syncVideos();
        },
        { signal: this.signal }
      );
      document.addEventListener('visibilitychange', () => this.syncVideos(), { signal: this.signal });

      if (!this.loaded) {
        // The film starts after window load AND after the first paint, so it never competes with the
        // poster and the first screen (on fast connections load can fire before anything has painted).
        const start = () => {
          if (this.loaded || this.signal.aborted) return;
          this.loaded = true;
          this.syncVideos();
        };
        window.addEventListener('load', () => afterFirstPaint(start), { once: true, signal: this.signal });
      }

      const onMotion = () => {
        if (reducedMotion()) {
          this.userPaused = true;
          this.syncVideos();
        }
        this.syncSnap();
      };
      this.motionQuery.addEventListener('change', onMotion, { signal: this.signal });
    }

    update() {
      if (!this.count || !this.isConnected) return;
      const rect = this.section.getBoundingClientRect();
      const viewport = window.innerHeight;
      const slot = rect.height / this.count || viewport;
      const index = Math.max(0, Math.min(this.count - 1, Math.round(-rect.top / slot)));
      const inView = rect.bottom > viewport * IN_VIEW_BOTTOM && rect.top < viewport * IN_VIEW_TOP;

      if (this.rendered && index === this.index && inView === this.inView) return;
      this.rendered = true;
      this.index = index;
      this.inView = inView;
      this.render();
    }

    render() {
      const tone = this.tones[this.index];
      const hasVideo = Boolean(this.videos[this.index]);

      if (this.inView) document.body.dataset.stageTone = tone;
      else delete document.body.dataset.stageTone;

      this.classList.toggle('is-video', hasVideo);

      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      this.syncVideos();
      this.syncSnap();
      this.announce();
    }

    announce() {
      document.dispatchEvent(
        new CustomEvent('r7:stage-change', {
          detail: {
            inView: this.inView,
            index: this.index,
            tone: this.tones?.[this.index] ?? null,
            hasVideo: Boolean(this.videos?.[this.index]),
          },
        })
      );
    }

    /* ---------- Rail ---------- */

    bindRail() {
      this.dots.forEach((dot) => {
        dot.addEventListener(
          'click',
          () => this.goTo(Number(dot.dataset.index) || 0, reducedMotion() ? 'auto' : 'smooth'),
          { signal: this.signal }
        );
      });
    }

    slotTop(index) {
      const rect = this.section.getBoundingClientRect();
      return Math.round(rect.top + window.scrollY + index * (this.section.offsetHeight / this.count));
    }

    goTo(index, behavior) {
      window.scrollTo({ top: this.slotTop(index), behavior });
    }

    /* A still fill (0..1); stops the fill's animation if it has one. */
    setFill(index, progress) {
      const fill = this.fills[index];
      if (!fill) return;
      if (this.fillAnimation && this.fillAnimation.r7Fill === fill) this.stopFillAnimation();
      const value = Number.isFinite(progress) ? Math.max(0, Math.min(1, progress)) : 0;
      fill.style.transform = `scaleY(${value.toFixed(4)})`;
    }

    stopFillAnimation() {
      if (this.fillAnimation) this.fillAnimation.cancel();
      this.fillAnimation = null;
    }

    /* The film's position in its loop, in ms (null before its duration is known). */
    filmTime(video) {
      const duration = video.duration;
      if (!(duration > 0) || !Number.isFinite(duration)) return null;
      return (video.currentTime % duration) * 1000;
    }

    /* Rail fills: inactive bars empty, an image screen full, a video screen follows its film. */
    syncFill() {
      if (!this.fills || !this.fills.length) return;
      this.fills.forEach((fill, i) => {
        if (i !== this.index) this.setFill(i, 0);
      });
      const fill = this.fills[this.index];
      if (!fill) return;
      const video = this.videos[this.index];
      if (!video) {
        this.setFill(this.index, 1);
        return;
      }

      const time = this.filmTime(video);
      if (time === null || typeof fill.animate !== 'function') {
        this.setFill(this.index, time === null ? 0 : time / (video.duration * 1000));
        return;
      }

      let animation = this.fillAnimation;
      if (!animation || animation.r7Fill !== fill || animation.r7Duration !== video.duration) {
        this.stopFillAnimation();
        animation = fill.animate(FILL_FRAMES, {
          duration: video.duration * 1000,
          iterations: Infinity,
          easing: 'linear',
        });
        animation.r7Fill = fill;
        animation.r7Duration = video.duration;
        this.fillAnimation = animation;
      }

      /* Running only while the film really advances (not paused, seeking or stalled for data). */
      const running = this.inView && !video.paused && !video.seeking && video.readyState > 2;
      if (running && animation.playState !== 'running') animation.play();
      else if (!running && animation.playState !== 'paused') animation.pause();
      const rate = video.playbackRate > 0 ? video.playbackRate : 1;
      if (animation.playbackRate !== rate) animation.playbackRate = rate;
      animation.currentTime = time;
    }

    /* 'timeupdate' on the active film: put a running fill back on the film's time when they drift apart
       (a stall the 'waiting' event missed, a clock difference); without an animation, sync the fill. */
    onTimeUpdate(video) {
      const animation = this.fillAnimation;
      if (!animation) {
        this.syncFill();
        return;
      }
      if (animation.playState !== 'running' || video.paused) return;
      const time = this.filmTime(video);
      if (time === null) return;
      const loop = animation.r7Duration * 1000;
      const current = Number(animation.currentTime) % loop;
      const drift = Math.abs(current - time);
      if (Math.min(drift, loop - drift) > DRIFT_MS) animation.currentTime = time;
    }

    /* ---------- Video ---------- */

    bindVideos() {
      this.videos.forEach((video, i) => {
        if (!video) return;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.addEventListener(
          'playing',
          () => {
            this.screens[i].classList.add('is-playing');
            if (i === this.index) this.syncFill();
          },
          { signal: this.signal }
        );
        FILL_SYNC_EVENTS.forEach((type) => {
          video.addEventListener(
            type,
            () => {
              if (i === this.index) this.syncFill();
            },
            { signal: this.signal }
          );
        });
        video.addEventListener(
          'timeupdate',
          () => {
            if (i === this.index) this.onTimeUpdate(video);
          },
          { passive: true, signal: this.signal }
        );
      });
    }

    source(video) {
      return this.wide.matches
        ? video.dataset.srcLarge || video.dataset.srcSmall
        : video.dataset.srcSmall || video.dataset.srcLarge;
    }

    attach(video) {
      if (video.getAttribute('src')) return true;
      const src = this.source(video);
      if (!src) return false;
      video.preload = 'metadata';
      video.src = src;
      // The phone file may be a rendition URL derived in 7r-home-stage.liquid; if it can't load, use the large one.
      const large = video.dataset.srcLarge;
      if (large && src !== large) {
        video.addEventListener(
          'error',
          () => {
            if (video.getAttribute('src') !== src) return;
            video.src = large;
            this.syncVideos();
          },
          { once: true, signal: this.signal }
        );
      }
      return true;
    }

    play(video, force) {
      if (!this.loaded && !force) return;
      if (!this.attach(video) || !video.paused) return;
      const attempt = video.play();
      if (attempt && attempt.catch) {
        attempt.catch((error) => {
          if (error && error.name === 'NotAllowedError') {
            this.userPaused = true;
            this.renderToggle();
          }
        });
      }
    }

    syncVideos(force) {
      if (!this.count) return;
      const canPlay = this.inView && !this.userPaused && document.visibilityState === 'visible';

      this.videos.forEach((video, i) => {
        if (!video) return;
        if (i === this.index && canPlay) this.play(video, force);
        else if (!video.paused) video.pause();
      });

      /* Warm up the next video screen once the page has loaded (metadata only). */
      const next = this.videos[this.index + 1];
      if (next && this.loaded && this.inView && !this.userPaused) this.attach(next);

      this.renderToggle();
      this.syncFill();
    }

    bindToggle() {
      if (!this.toggle) return;
      this.toggle.addEventListener(
        'click',
        () => {
          this.userPaused = !this.userPaused;
          this.syncVideos(true);
        },
        { signal: this.signal }
      );
      this.renderToggle();
    }

    renderToggle() {
      const toggle = this.toggle;
      if (!toggle) return;
      const paused = this.userPaused;
      toggle.textContent = paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
      toggle.setAttribute('aria-label', paused ? toggle.dataset.playAria : toggle.dataset.pauseAria);
    }

    /* ---------- Scroll snap ---------- */

    syncSnap() {
      const on = this.snapEnabled && this.inView && !designMode() && !reducedMotion();
      if (on === this.snapOn) return;
      this.snapOn = on;
      document.documentElement.style.scrollSnapType = on ? 'y proximity' : '';
    }

    /* ---------- Keyboard focus into a stacked screen ---------- */

    bindFocus() {
      this.addEventListener(
        'focusin',
        (event) => {
          const target = event.target;
          if (!(target instanceof Element) || !target.matches(':focus-visible')) return;
          const screen = target.closest('[data-r7-stage-screen]');
          const index = this.screens.indexOf(screen);
          if (index === -1) return;
          if (Math.abs(window.scrollY - this.slotTop(index)) > 2) this.goTo(index, 'auto');
        },
        { signal: this.signal }
      );
    }

    /* ---------- Theme editor ---------- */

    bindEditor() {
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const blockId = event.detail?.blockId;
          const index = this.screens.findIndex((screen) => screen.dataset.blockId === blockId);
          if (index !== -1) this.goTo(index, 'auto');
        },
        { signal: this.signal }
      );
    }

    /* ---------- Under-header offset (hero pattern) ---------- */

    syncLead() {
      const section = this.section;
      if (!section || !section.hasAttribute('data-overlap-header')) return;
      const wrapper = section.closest('.shopify-section');
      const first = wrapper?.closest('main')?.querySelector(':scope > .shopify-section');
      section.classList.toggle('r7-stage--lead', Boolean(wrapper) && wrapper === first);
    }

    bindSections() {
      const sync = () =>
        requestAnimationFrame(() => {
          this.syncLead();
          this.update();
        });
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) =>
        document.addEventListener(type, sync, { signal: this.signal })
      );
    }
  }

  customElements.define('r7-stage', R7Stage);
})();
